import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from './components/HeroSection';
import BenefitsSection from './components/BenefitsSection';
import HowItWorksSection from './components/HowItWorksSection';
import MetricsSection from './components/MetricsSection';
import TestimonialsSection from './components/TestimonialsSection';
import ComparisonSection from './components/ComparisonSection';
import PricingSection from './components/PricingSection';
import FAQSection from './components/FAQSection';
import SEOHead from '../../components/feature/SEOHead';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://helixring.com';

const homeSchemas = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${SITE_URL}/#webpage`,
    name: 'HELIX Ring | Anillo Inteligente Premium | Salud y Actividad 24/7',
    description:
      'HELIX Ring, el anillo inteligente premium que monitorea ritmo cardíaco, sueño y actividad diaria con precisión médica. Diseño titanio ultraligero, batería 7 días, IP68. Reserva tu preventa exclusiva.',
    url: SITE_URL,
    inLanguage: 'es',
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      name: 'HELIX Ring',
      url: SITE_URL,
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Inicio',
          item: SITE_URL,
        },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'HELIX',
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.jpeg`,
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'hola@helixring.com',
      contactType: 'customer service',
      availableLanguage: 'Spanish',
    },
    sameAs: [],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'HELIX Ring',
    url: SITE_URL,
    description:
      'Anillo inteligente premium para monitoreo de salud continuo: ritmo cardíaco, sueño, actividad y más.',
    inLanguage: 'es',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'HELIX Ring',
    description:
      'Anillo inteligente premium de titanio para monitoreo continuo de ritmo cardíaco, sueño, actividad diaria y más. Certificación IP68, batería 7 días, sincronización por Bluetooth 5.0.',
    brand: { '@type': 'Brand', name: 'HELIX' },
    material: 'Titanio',
    url: SITE_URL,
    offers: [
      {
        '@type': 'Offer',
        name: 'HELIX Esencial',
        priceCurrency: 'EUR',
        price: '12',
        availability: 'https://schema.org/PreOrder',
        seller: { '@type': 'Organization', name: 'HELIX' },
      },
      {
        '@type': 'Offer',
        name: 'HELIX Premium',
        priceCurrency: 'EUR',
        price: '79.99',
        availability: 'https://schema.org/PreOrder',
        seller: { '@type': 'Organization', name: 'HELIX' },
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '¿Cómo sé qué talla de anillo necesito?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Incluimos un kit de medición gratuito con tu pedido. También puedes pedir solo el kit de tallas antes de comprometer tu reserva. Cubrimos las tallas del 5 al 13 (US), que equivalen a los tamaños europeos del 49 al 68.',
        },
      },
      {
        '@type': 'Question',
        name: '¿HELIX es compatible con mi smartphone?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sí. La app HELIX es compatible con iOS 15 o superior y Android 10 o superior. Cualquier teléfono fabricado después de 2019 es totalmente compatible. La sincronización se hace por Bluetooth 5.0 de bajo consumo.',
        },
      },
      {
        '@type': 'Question',
        name: '¿Qué tan preciso es el monitor de ritmo cardíaco?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Nuestros sensores ópticos PPG de cuarta generación ofrecen una precisión del 99.2% en comparación con monitores clínicos certificados. Los algoritmos han sido validados en un estudio con más de 3,000 participantes.',
        },
      },
      {
        '@type': 'Question',
        name: '¿Puedo ducharme y nadar con HELIX?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Completamente. HELIX tiene certificación IP68, lo que significa resistencia al agua hasta 100 metros de profundidad. Es apto para ducha, piscina, surf y snorkel. El titanio no se oxida ni se decolora.',
        },
      },
      {
        '@type': 'Question',
        name: '¿Cuánto tiempo dura la batería?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Con el monitoreo continuo activado, la batería dura entre 6 y 8 días. En modo ultra-ahorro puede alcanzar los 12 días. La carga completa desde cero toma 80 minutos con el cargador magnético incluido.',
        },
      },
      {
        '@type': 'Question',
        name: '¿Cuándo se entregan los pedidos de preventa?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Los fundadores recibirán sus unidades en el primer lote de envíos, estimado para el primer trimestre de 2025. Recibirás actualizaciones mensuales del estado de producción y tu número de seguimiento en cuanto se procese tu envío.',
        },
      },
      {
        '@type': 'Question',
        name: '¿Qué pasa si no me gusta o no me funciona?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Tienes 30 días desde la recepción para devolver HELIX sin ningún coste y sin dar explicaciones. El reembolso es completo e inmediato. Además, todos los anillos incluyen garantía de fabricación de 1 año (2 años para fundadores).',
        },
      },
      {
        '@type': 'Question',
        name: '¿HELIX reemplaza a un médico o a un diagnóstico clínico?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'HELIX es un dispositivo de bienestar personal, no un dispositivo médico certificado. Los datos son de alta precisión y de gran valor informativo, pero no reemplazan el criterio médico profesional. Si detectas irregularidades, consulta siempre a tu médico.',
        },
      },
    ],
  },
];

const HomePage = () => (
  <main className="w-full min-w-0 overflow-x-hidden">
    <SEOHead schemas={homeSchemas} />
    <Navbar />
    <HeroSection />
    <BenefitsSection />
    <HowItWorksSection />
    <MetricsSection />
    <TestimonialsSection />
    <ComparisonSection />
    <PricingSection />
    <FAQSection />
    <Footer />
  </main>
);

export default HomePage;
